/**
 * Payment Session Blocking Hook
 *
 * Detects active payment sessions for a case.
 * Use with `getPaymentTabVisibility()` to apply blocking matrix logic.
 */

import { useEffect, useState } from 'react';
import { STORAGE_KEYS } from '@ophelos/shared/constants/storage-keys';
import { listPaymentSessionsFromConnect, type PaymentSessionDto } from '@/lib/connect-api';

export type PaymentType = 'full' | 'partial' | 'payment_plan';

export interface ActiveSessionState {
  /**
   * Active session currently blocking other payment types (status='processing')
   */
  activeSession: PaymentSessionDto | null;

  /**
   * Loading state for initial session query
   */
  isLoading: boolean;

  /**
   * Error if session query failed
   */
  error: Error | null;
}

/**
 * Hook to fetch active payment session for a case
 *
 * Queries for 'processing' status sessions only (user actively in Ophelos checkout).
 * Other statuses don't block:
 * - 'pending' = Not yet redirected to Ophelos (can be reused)
 * - 'incomplete' = User cancelled (can be retried)
 * - 'completed' = Payment done (terminal)
 * - 'expired' = Timed out (terminal)
 *
 * @param caseId - Case ID to check for active sessions
 * @param getAccessToken - Token getter for authStage 'full' requests
 * @returns Active session state with loading/error
 */
export function useActivePaymentSession(
  caseId: string | undefined,
  getAccessToken?: () => Promise<string | null>,
): ActiveSessionState {
  const [activeSession, setActiveSession] = useState<PaymentSessionDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!caseId) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    async function fetchActiveSession() {
      try {
        setIsLoading(true);
        setError(null);

        // Query for active 'processing' session
        // Only 'processing' status triggers blocking (user actively paying)
        const authStage = (localStorage.getItem(STORAGE_KEYS.AUTH_STAGE) as 'limited' | 'full' | null) ?? 'limited';
        const sessions = await listPaymentSessionsFromConnect({ caseId, statuses: ['processing'] }, authStage, getAccessToken);

        if (!isMounted) return;

        setActiveSession(sessions[0] ?? null);
        setIsLoading(false);
      } catch (err) {
        if (!isMounted) return;
        console.error('[useActivePaymentSession] Unexpected error:', {
          case_id: caseId,
          error: err,
        });
        setError(err instanceof Error ? err : new Error('Failed to check active payment session'));
        setIsLoading(false);
        // Non-fatal: Allow all tabs if we can't check (fail-open for UX)
        setActiveSession(null);
      }
    }

    fetchActiveSession();

    return () => {
      isMounted = false;
    };
  }, [caseId, getAccessToken]);

  return {
    activeSession,
    isLoading,
    error,
  };
}
